// Settings → Emergency Numbers → Bandwidth: one OAuth 2.0 credential, two account
// numbers (static / DLR), the cached token's state, and a read-only live check.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2, RefreshCw, TriangleAlert, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SIDES = [
  ["static", "bandwidth_static_account", "Static account", "Unified Emergency Services API — static 911 registrations (list / prove / register / remove)."],
  ["dlr", "bandwidth_dlr_account", "DLR account", "Numbers API AEUIs — the DLR records Bandwidth holds for every Emergency Endpoint (read-only)."],
];
const fmt = (n) => Number(n || 0).toLocaleString();
const mins = (s) => `${Math.max(0, Math.round((s || 0) / 60))} min`;
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

function TokenLine({ token, credential }) {
  if (!credential?.configured) {
    return <span className="text-amber-700" data-testid="e911-bw-token-missing">No Bandwidth credential yet — add it under API Connections.</span>;
  }
  if (!token?.cached) {
    return <span className="text-slate-500" data-testid="e911-bw-token-none">No token in memory — one is requested from Bandwidth on the next call and then reused until it expires.</span>;
  }
  return (
    <span className={token.valid ? "text-emerald-700" : "text-amber-700"} data-testid="e911-bw-token">
      Token {token.valid ? `valid for ${mins(token.seconds_left)} more (until ${clock(token.expires_at)}, renewed at ${clock(token.renews_at)})` : "expired — renewed on the next call"}
      {" · "}minted {clock(token.minted_at)}
      {!!token.accounts?.length && <> · accounts on the credential: <strong>{token.accounts.join(", ")}</strong></>}
      {!!token.roles?.length && <span className="text-slate-500"> · {token.roles.length} roles</span>}
    </span>
  );
}

function AccountResult({ side, status, result }) {
  const onToken = result?.on_token ?? status?.on_token;
  if (!result && onToken === false) {
    return <span className="text-[11px] text-amber-700" data-testid={`e911-bw-${side}-not-on-token`}><TriangleAlert className="mr-1 inline h-3 w-3" /> not on the credential's account list</span>;
  }
  if (!result) return null;
  if (result.ok) {
    return (
      <span className="text-[11px] text-emerald-700" data-testid={`e911-bw-${side}-ok`}>
        <CheckCircle2 className="mr-1 inline h-3 w-3" />
        {fmt(result.count)} {side === "static" ? "static registration(s)" : "DLR record(s)"}
        {onToken === false && <span className="ml-1 text-amber-700">· not on the credential's account list</span>}
      </span>
    );
  }
  return <span className="text-[11px] text-rose-700" data-testid={`e911-bw-${side}-fail`}><XCircle className="mr-1 inline h-3 w-3" /> {result.error || "failed"}</span>;
}

export default function E911BandwidthCard({ cfg, set }) {
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const loadStatus = () => api.get("/e911/bandwidth").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/e911/bandwidth/check", {
        static_account: cfg.bandwidth_static_account || "", dlr_account: cfg.bandwidth_dlr_account || "" });
      setResult(data);
      await loadStatus();
      const bad = ["static", "dlr"].filter((s) => !data[s]?.ok);
      if (bad.length) toast.error(`Bandwidth check: ${bad.join(" + ")} account not answering`);
      else toast.success(`Bandwidth answers for both accounts — ${fmt(data.static.count)} static, ${fmt(data.dlr.count)} DLR`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach Bandwidth");
    } finally {
      setChecking(false);
    }
  };

  const credOk = status?.credential?.configured;
  return (
    <Card className="space-y-4 px-4 py-4" data-testid="e911-settings-bandwidth">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <KeyRound className="h-4 w-4 text-slate-500" /> Bandwidth
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${credOk ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                  data-testid="e911-bw-cred-chip">{credOk ? "credential connected" : "no credential"}</span>
          </div>
          <p className="mt-1 max-w-3xl text-[12px] leading-relaxed text-slate-500">
            One OAuth 2.0 client credential (Settings → API Connections → <em>Bandwidth — API</em>) covers every account it is
            scoped to; Bandwidth lists them inside the token. Each API call carries the account number below, so the two
            accounts must be set here. The token is kept in memory and reused until it expires, then a new one is requested
            with the Client ID + Secret — a rejected call also forces a fresh token once.
          </p>
        </div>
        <Link to="/settings?tab=api-connections" className="text-xs font-medium text-primary hover:underline" data-testid="e911-bw-connections-link">
          API Connections →
        </Link>
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[12px]" data-testid="e911-bw-token-line">
        <TokenLine token={result?.token || status?.token} credential={status?.credential} />
        {status?.credential?.token_url && (
          <div className="mt-0.5 font-mono text-[10px] text-slate-400">{status.credential.base_url} · token from {status.credential.token_url}</div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {SIDES.map(([side, key, label, hint]) => (
          <div key={side}>
            <Label className="text-xs text-slate-500">{label}</Label>
            <Input value={cfg[key] || ""} onChange={set(key)} placeholder="Bandwidth account number" inputMode="numeric"
                   className="font-mono" data-testid={`e911-settings-bw-${side}`} />
            <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
            <div className="mt-1 min-h-[16px]"><AccountResult side={side} status={status?.accounts?.[side]} result={result?.[side]} /></div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-400">The check is read-only: it reuses or mints the token and counts what each account holds.</p>
        <Button size="sm" variant="outline" onClick={check} disabled={checking || !credOk} data-testid="e911-bw-check">
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Check accounts
        </Button>
      </div>
    </Card>
  );
}
